/**
 * @reflecto/core — safetyNumber.ts
 *
 * Pairwise encryption fingerprint ("safety number") — a 60-decimal-digit
 * value computed independently by both devices of a pair from the two public
 * keys they hold. Displayed in settings so a user can verify, in person, that
 * no man-in-the-middle sat inside the pairing handshake.
 *
 * Derived from PUBLIC keys only — no secret material is ever touched, so the
 * output is safe to display, copy, screenshot, or read aloud.
 *
 * INTEROP: this is the source of truth for the algorithm. The Kotlin
 * counterpart (SafetyNumberCalculator.kt, java.security.MessageDigest) must
 * produce byte-identical output. See docs/superpowers/specs/2026-07-13-safety-numbers-design.md
 * §3 (locked algorithm) + §4 (canonical vectors). Divergence = stop-everything.
 *
 * This is NOT the wire format. It does not touch crypto.ts, encryptMessage,
 * decryptMessage, or WIRE_VERSION — nothing here travels over the wire.
 */

import nacl from "tweetnacl";
import { decodeUTF8 } from "tweetnacl-util";

// Domain separation from any other hash use; `v1` allows algorithm rotation
// without ambiguity. Any change here is a breaking change requiring a `v2` tag
// and a synchronized Kotlin update — never change one side alone.
const DOMAIN_TAG = "reflecto-safety-v1";

// Signal's constant. Grinding a look-alike keypair costs 5200 hashes per
// candidate; negligible for two devices computing once per screen-view.
const ITERATIONS = 5200;

const KEY_LENGTH = 32; // X25519 public key

// 12 groups × 5 decimal digits = the 60-digit fingerprint. Each group is a
// 5-byte big-endian chunk of the digest reduced mod 100000, so 60 of the 64
// digest bytes are consumed.
const GROUP_COUNT = 12;
const CHUNK_BYTES = 5;
const CHUNK_MODULUS = 100000; // 10^GROUP_DIGITS

const TAG_BYTES = decodeUTF8(DOMAIN_TAG);

/**
 * Compare two byte arrays by unsigned lexicographic order.
 * Returns < 0 if a sorts before b, > 0 if after, 0 if equal.
 * Both inputs are 32-byte keys, so the length tiebreak never fires here.
 */
function compareUnsigned(a: Uint8Array, b: Uint8Array): number {
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) {
    if (a[i] !== b[i]) {
      return a[i] - b[i];
    }
  }
  return a.length - b.length;
}

/**
 * Read a 5-byte big-endian chunk as an unsigned integer.
 * uint40 fits in a JS number (2^40 < 2^53), so plain arithmetic — no BigInt.
 * Multiplication (not `<<`) avoids JS's 32-bit signed bitwise coercion.
 */
function uint40BigEndian(bytes: Uint8Array, offset: number): number {
  let value = 0;
  for (let i = 0; i < CHUNK_BYTES; i++) {
    value = value * 256 + bytes[offset + i];
  }
  return value;
}

/**
 * Compute the pairwise safety number for two X25519 public keys.
 *
 * Symmetric: the keys are sorted before hashing, so both sides compute over
 * identical bytes without coordinating who is "first" —
 * computeSafetyNumber(a, b) === computeSafetyNumber(b, a).
 *
 * @param keyA - one party's 32-byte X25519 public key
 * @param keyB - the other party's 32-byte X25519 public key
 * @returns the ungrouped 60-digit ASCII fingerprint (grouping is display-layer)
 * @throws if either key is not exactly 32 bytes
 */
export function computeSafetyNumber(keyA: Uint8Array, keyB: Uint8Array): string {
  if (keyA.length !== KEY_LENGTH) {
    throw new Error(`Invalid public key: expected ${KEY_LENGTH} bytes, got ${keyA.length}`);
  }
  if (keyB.length !== KEY_LENGTH) {
    throw new Error(`Invalid public key: expected ${KEY_LENGTH} bytes, got ${keyB.length}`);
  }

  // Unsigned lexicographic sort makes the function order-independent.
  const [first, second] = compareUnsigned(keyA, keyB) <= 0 ? [keyA, keyB] : [keyB, keyA];

  // h = TAG || first || second  (18 + 32 + 32 = 82 bytes)
  const seed = new Uint8Array(TAG_BYTES.length + KEY_LENGTH + KEY_LENGTH);
  seed.set(TAG_BYTES, 0);
  seed.set(first, TAG_BYTES.length);
  seed.set(second, TAG_BYTES.length + KEY_LENGTH);

  // First iteration hashes the 82-byte seed; every later iteration hashes the
  // prior 64-byte SHA-512 digest.
  let h = nacl.hash(seed);
  for (let i = 1; i < ITERATIONS; i++) {
    h = nacl.hash(h);
  }

  // 12 groups of 5 digits: each a 5-byte big-endian uint40 reduced mod 100000,
  // zero-padded to 5 characters, concatenated into the 60-digit string.
  let digits = "";
  for (let g = 0; g < GROUP_COUNT; g++) {
    const chunk = uint40BigEndian(h, g * CHUNK_BYTES) % CHUNK_MODULUS;
    digits += chunk.toString().padStart(5, "0");
  }

  return digits;
}

// No `formatSafetyNumber` here on purpose: grouping the digits for display is
// display-layer formatting (spec §3), and it now has exactly one owner — the
// `SafetyNumberCard`, which hands the grouped string it renders straight to
// its `onCopy`. A second copy in core would be a grouping impl no one calls,
// and one the clipboard string could silently drift from.
